import React from 'react';
import { Descriptions, Space, Tag, Typography } from 'antd';
import type { CorrectionRouteContextView } from './buildCorrectionRouteContextView';
import { formatCircledOrdinal } from './buildCorrectionRouteContextView';

const { Text } = Typography;

type Props = {
  model: CorrectionRouteContextView;
};

function RouteItineraryList({ stops }: { stops: CorrectionRouteContextView['stops'] }) {
  if (stops.length === 0) {
    return <Text type="secondary">Точки маршрута не заданы</Text>;
  }

  return (
    <div className="correction-route-context__itinerary">
      {stops.map((stop, idx) => (
        <div key={stop.stopId ?? `stop-${idx}`} className="correction-route-context__stop">
          <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
            <Text strong>
              {formatCircledOrdinal(stop.ordinal - 1)} {stop.typeLabel}
            </Text>
            <Tag
              color={
                stop.band === 'COMPLETED' ? 'success' : stop.band === 'CURRENT' ? 'processing' : 'default'
              }
            >
              {stop.statusLabel}
            </Tag>
          </Space>
          {stop.applicationLabel ? (
            <Text style={{ display: 'block', marginTop: 4 }}>{stop.applicationLabel}</Text>
          ) : null}
          {stop.groupLabel ? (
            <Tag style={{ marginTop: 4 }}>{stop.groupLabel}</Tag>
          ) : null}
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            {stop.addressLine}
          </Text>
          {stop.plannedLabel ? (
            <Text style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
              План: {stop.plannedLabel}
            </Text>
          ) : null}
          {stop.actualLabel ? (
            <Text style={{ display: 'block', fontSize: 12 }}>
              Факт: {stop.actualLabel}
            </Text>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function CorrectionRouteContextPanelInner({ model }: Props) {
  return (
    <div className="correction-route-context">
      <div className="correction-route-context__section-title">Маршрут</div>
      {model.executionTags.length > 0 ? (
        <Space wrap style={{ marginBottom: 8 }}>
          {model.executionTags.map((t) => (
            <Tag key={t.text} color={t.color}>
              {t.text}
            </Tag>
          ))}
        </Space>
      ) : null}

      <Descriptions size="small" column={1} bordered className="correction-route-context__summary">
        <Descriptions.Item label="№">{model.routeNumber}</Descriptions.Item>
        <Descriptions.Item label="Статус">{model.statusLabel}</Descriptions.Item>
        <Descriptions.Item label="Перевозчик">{model.carrierLabel ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Водитель">{model.driverLabel ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Авто / прицеп">{model.vehicleLabel ?? '—'}</Descriptions.Item>
      </Descriptions>

      {model.financeSummary.lines.length > 0 ? (
        <>
          <div className="correction-route-context__section-title">Финансы</div>
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
            {model.financeSummary.lines.map((line) => (
              <Descriptions.Item key={line.label} label={line.label}>
                {line.value}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </>
      ) : null}

      <div className="correction-route-context__section-title">Точки маршрута</div>
      <RouteItineraryList stops={model.stops} />
    </div>
  );
}

export const CorrectionRouteContextPanel = React.memo(CorrectionRouteContextPanelInner);
